import { ConsoleSession } from "@cloudserver/contracts";
import type RFB from "@novnc/novnc";
import { api, ApiProblem } from "../../../api/client.js";
import { screenSettings, type ScaleMode } from "../../../terminal/screen-settings.js";
import { PANE_PADDING } from "../../../terminal/geometry.js";
import { consoleTabOf, terminals } from "../../../terminal/store.js";

/*
 * One server console (VNC through our API, docs/console.md) per tab, outside
 * React like the terminals and desktops: noVNC's RFB draws into an element
 * that moves between the workspace and a hidden parking spot, so the
 * console keeps running while the person looks elsewhere. noVNC loads on
 * the first console only.
 */

/** Pause between typed characters: servers drop keys sent faster than they poll. */
const TYPE_DELAY_MS = 12;
/**
 * How long after a reboot or reset from the console's «Питание» a lost
 * console is the server restarting, and comes back by itself.
 */
export const RESTART_WAIT_MS = 120_000;
/** Between two attempts to get the console back while the server restarts. */
const RESTART_RETRY_MS = 3000;

const socketUrl = (path: string) =>
  `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${path}`;

/** Where consoles wait while no workspace shows them. */
function parking(): HTMLElement {
  let element = document.getElementById("vnc-parking");
  if (!element) {
    element = document.createElement("div");
    element.id = "vnc-parking";
    element.setAttribute("aria-hidden", "true");
    element.inert = true;
    Object.assign(element.style, {
      position: "fixed",
      left: "-10000px",
      top: "0",
      width: "1024px",
      height: "768px",
      visibility: "hidden",
      overflow: "hidden",
    });
    document.body.append(element);
  }
  return element;
}

export class VncConnection {
  /** noVNC puts its screen here and scales it to this box. */
  readonly element = document.createElement("div");
  private rfb: RFB | null = null;
  private disposed = false;
  /** The person ended it («Отключиться»): a clean end, not a failure. */
  private ending = false;
  /** Until when (epoch ms) a lost console is the server restarting: it reconnects by itself. */
  private restartUntil = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly stopScale: () => void;

  constructor(readonly id: string) {
    this.element.className = "absolute inset-0";
    this.element.dataset.vnc = id;
    parking().append(this.element);
    this.stopScale = screenSettings.subscribe(() => this.applyScale());
    this.applyScale();
  }

  /** Opens a new console session: the API's ticket, then noVNC over our WebSocket. */
  async connect(): Promise<void> {
    const tab = terminals.tab(this.id);
    if (!tab || this.disposed) return;
    this.clearRetry();
    this.teardown();
    terminals.update(this.id, { status: "connecting", failure: null, problem: null });
    let session: ConsoleSession;
    let Rfb: typeof RFB;
    try {
      [session, { default: Rfb }] = await Promise.all([
        api(`/projects/${tab.projectId}/servers/${tab.serverId}/console`, {
          method: "POST",
          schema: ConsoleSession,
        }),
        import("@novnc/novnc"),
      ]);
    } catch (error) {
      // Restarting: the server may refuse a console for a moment.
      if (this.retryWhileRestarting()) return;
      // The API refused (server off, too many consoles, no access…).
      terminals.update(this.id, {
        status: "disconnected",
        failure: null,
        problem:
          error instanceof ApiProblem && error.problem
            ? (error.problem.reason ?? error.problem.code)
            : "unexpected",
      });
      return;
    }
    if (this.disposed) return;
    const client = new Rfb(this.element, socketUrl(session.url), {
      ...(session.password ? { credentials: { password: session.password } } : {}),
    });
    client.background = "transparent";
    client.resizeSession = false;
    client.addEventListener("connect", () => {
      terminals.update(this.id, { status: "connected" });
      this.applyScale();
    });
    client.addEventListener("disconnect", (event) => {
      if (this.rfb !== client) return;
      const clean = (event as CustomEvent<{ clean: boolean }>).detail.clean;
      this.rfb = null;
      if (!this.ending && this.retryWhileRestarting()) return;
      terminals.update(this.id, {
        status: "disconnected",
        failure: clean || this.ending ? "closed" : "failed",
      });
      this.ending = false;
    });
    this.rfb = client;
    this.applyScale();
  }

  /**
   * A reboot or reset was asked from the console's «Питание»: when the
   * console drops in the next two minutes, it comes back by itself.
   */
  expectRestart(): void {
    this.restartUntil = Date.now() + RESTART_WAIT_MS;
  }

  /** Lost while the server restarts: shown as connecting, tried again shortly. */
  private retryWhileRestarting(): boolean {
    if (this.disposed || Date.now() > this.restartUntil) return false;
    terminals.update(this.id, { status: "connecting", failure: null, problem: null });
    this.clearRetry();
    this.retryTimer = setTimeout(() => void this.connect(), RESTART_RETRY_MS);
    return true;
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  /**
   * «Вписать»: the remote screen is scaled into the box inside the same even
   * padding as a terminal's grid (`PANE_PADDING`), letterboxed in the
   * screen's colour (noVNC centres its canvas; owner's screenshot of
   * 2026-10-22: SeaBIOS text touched the edge). «1:1» keeps the whole box
   * and scrolls.
   */
  private applyScale(): void {
    const scale: ScaleMode = screenSettings.snapshot().scale;
    this.element.style.padding = scale === "fit" ? `${PANE_PADDING}px` : "";
    const client = this.rfb;
    if (!client) return;
    client.scaleViewport = scale === "fit";
    client.clipViewport = scale === "actual";
    client.dragViewport = scale === "actual" && matchMedia("(pointer: coarse)").matches;
  }

  /** Presses and releases a key, with modifiers held around it. */
  press(keysym: number, code: string | null = null, held: readonly number[] = []): void {
    const client = this.rfb;
    if (!client) return;
    for (const modifier of held) client.sendKey(modifier, null, true);
    client.sendKey(keysym, code, true);
    client.sendKey(keysym, code, false);
    for (const modifier of [...held].reverse()) client.sendKey(modifier, null, false);
  }

  /** Types keysyms one by one; resolves when all are sent. */
  async type(keysyms: readonly number[]): Promise<void> {
    for (const keysym of keysyms) {
      const client = this.rfb;
      if (!client) return;
      client.sendKey(keysym, null, true);
      client.sendKey(keysym, null, false);
      await new Promise((resolve) => setTimeout(resolve, TYPE_DELAY_MS));
    }
  }

  /** The screen as noVNC draws it, for the assistant's screen context; null before it drew. */
  snapshot(): HTMLCanvasElement | null {
    return this.rfb ? this.element.querySelector("canvas") : null;
  }

  ctrlAltDel(): void {
    this.rfb?.sendCtrlAltDel();
  }

  focus(): void {
    this.rfb?.focus({ preventScroll: true });
  }

  /** Shows the console in `pane`, or parks it when null (noVNC follows the size itself). */
  place(pane: HTMLElement | null): void {
    (pane ?? parking()).append(this.element);
  }

  /** Ends the session and keeps the tab («Отключиться»); reconnecting opens a new one. */
  disconnect(): void {
    this.restartUntil = 0;
    this.clearRetry();
    if (!this.rfb) return;
    this.ending = true;
    this.rfb.disconnect();
  }

  private teardown(): void {
    const client = this.rfb;
    this.rfb = null;
    client?.disconnect();
    this.element.replaceChildren();
  }

  dispose(): void {
    this.disposed = true;
    this.clearRetry();
    this.stopScale();
    this.teardown();
    this.element.remove();
  }
}

const connections = new Map<string, VncConnection>();

export const vncConnectionOf = (id: string): VncConnection | undefined => connections.get(id);

export interface OpenConsoleRequest {
  projectId: string;
  server: { id: string; name: string; osFlavor: string | null; osVersion: string | null };
}

/**
 * The server's console as a tab: focuses the one already open for this
 * server (reconnecting it when it ended), else opens a new tab. Returns its id.
 */
export function openConsole(request: OpenConsoleRequest): string {
  const existing = consoleTabOf(terminals.snapshot(), request.server.id);
  if (existing) {
    terminals.activate(existing.id);
    if (existing.status === "disconnected") void connections.get(existing.id)?.connect();
    return existing.id;
  }
  const id = crypto.randomUUID();
  terminals.add({
    id,
    kind: "vnc",
    projectId: request.projectId,
    serverId: request.server.id,
    serverName: request.server.name,
    osFlavor: request.server.osFlavor,
    osVersion: request.server.osVersion,
    unixUser: "",
    auth: "password",
    status: "connecting",
    failure: null,
    offeredHostKey: null,
    problem: null,
    hostKeyFingerprint: null,
    sessionId: null,
    unseen: false,
  });
  const connection = new VncConnection(id);
  connections.set(id, connection);
  void connection.connect();
  return id;
}

/** A console tab rebuilt after a reload (`terminal/restore.ts`): a new ticket, connected at once. */
export function resumeConsole(id: string): void {
  if (connections.has(id)) return;
  const connection = new VncConnection(id);
  connections.set(id, connection);
  void connection.connect();
}

/** Ends a tab's console, if it is one (closing the tab: `terminal/connections.ts`). */
export function disposeVnc(id: string): void {
  connections.get(id)?.dispose();
  connections.delete(id);
}
